import { makeStyles } from '@material-ui/core';
import { addDays, differenceInDays } from 'date-fns';
import React from 'react';
import { AlertOctagon, AlertTriangle, Calendar, Check, ChevronsRight, List, Loader } from 'react-feather';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import {
    enumToIcon,
    enumToStatusType,
    inspectionTypesCombined,
    probabilities,
    projectStatus,
} from '../../../types/common';
import { ProjectSummary } from '../../../types/project';
import { Badge, StatusIcon } from '../../global';
import { Drawer } from '../../global/Drawer';
import { FilterBar } from '../../global/FilterBar';
import { Table } from '../../global/Table';
import { ProjectEditor } from '../editor/ProjectEditor';
import { ProjectListCalendarView } from './ProjectListCalendarView';
import {
    ColumnAttributes,
    ColumnEquipment,
    ColumnRisk,
    ColumnStatus,
    ColumnTimeline,
    ColumnType,
} from './ProjectListColumns';
import { ProjectListStatusCell } from './ProjectListStatusCell';
import { ProjectListUpcomingBar } from './ProjectListUpcomingBar';

const useProjectListClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        background: theme.colors.neutral[6],
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        padding: 40,
        background: theme.colors.darkBlue[1],
        '& > span:first-of-type': {
            color: theme.colors.white,
            ...theme.typography.page.title,
            marginBottom: 0,
        },
        '& > span:last-of-type': {
            color: theme.colors.neutral[3],
            ...theme.typography.content.label.default,
        },
    },
    status: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 20,
        '& > div': {
            flex: 1,
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        gap: 16,
        padding: '24px 40px',
    },
    count: {
        color: theme.colors.neutral[2],
        ...theme.fonts.h5,
    },
    reflow: {
        display: 'flex',
        '& > div': {
            flex: 1,
            width: 0,
        },
    },
});

export const ProjectList = (props: { initialView?: 'table' | 'calendar' }) => {
    const classes = useProjectListClasses();
    const { t } = usePrefixTranslation(['project', 'common'], { project: 'list.ProjectList' });
    const navigate = useNavigate();
    const { view } = useParams();
    const dispatch = useDispatch();
    const { project, project_list, statusCounts } = useTypedSelector(state => ({
        project: state.project,
        project_list: state.project_list,
        statusCounts: state.stats.statusCounts,
    }));
    const [page, setPage] = React.useState(0);
    const extraParams = React.useRef<{ [param: string]: string }>({});
    const update = React.useReducer(() => ({}), {})[1];
    const [numberOfItemsPerPage, setNumberOfItemsPerPage] = React.useState(10);
    const [selectedProject, setSelectedProject] = React.useState<ProjectSummary>();

    React.useEffect(() => void dispatch(actions.stats.statusCounts()), [project.status]);

    React.useEffect(() => {
        project_list.filter === ''
            ? dispatch(actions.project_list.list({ filter: 'upcoming', page: 0, extraParams: extraParams.current }))
            : dispatch(actions.project_list.list({ page: 0, extraParams: extraParams.current }));
    }, []);

    const status = [
        {
            filter: 'past due',
            value: statusCounts.stats?.['past due'] ?? 0,
            label: t('common:status.past-due'),
            type: 'error',
            icon: <AlertOctagon />,
        },
        {
            filter: 'ready to inspect',
            value: statusCounts.stats?.['ready to inspect'] ?? 0,
            label: t('common:status.ready to inspect'),
            type: 'info',
            icon: <Calendar />,
        },
        {
            filter: 'in progress',
            value: statusCounts.stats?.['in progress'] ?? 0,
            label: t('common:status.in progress'),
            type: 'success',
            icon: <ChevronsRight />,
        },
        {
            filter: 'awaiting report',
            value: statusCounts.stats?.['awaiting report'] ?? 0,
            label: t('common:status.awaiting report'),
            type: 'warning',
            icon: <AlertTriangle />,
        },
        {
            filter: 'under review',
            value: statusCounts.stats?.['under review'] ?? 0,
            label: t('common:status.under review'),
            type: 'warning',
            icon: <Loader />,
        },
        {
            filter: 'completed',
            value: statusCounts.stats?.['completed'] ?? 0,
            label: t('common:status.completed'),
            type: 'neutral',
            icon: <Check />,
        },
    ] as const;

    const headers = [
        { key: 'name', element: t('table-header-equipment'), size: 'minmax(250px, 1fr)', sortable: true },
        { key: '', element: t('table-header-type'), size: '130px' },
        { key: 'status', element: t('table-header-status'), size: '180px', sortable: true },
        { key: '', element: t('table-header-dates-external'), size: '210px' },
        { key: '', element: t('table-header-dates-internal'), size: '210px' },
        { key: '', element: t('table-header-risk'), size: '160px' },
        { key: '', element: t('table-header-attributes'), size: '130px' },
    ];

    const selectProject = async (project: ProjectSummary) => {
        if (selectedProject != undefined) return;
        new Promise(resolve => setTimeout(resolve, 500)).then(() => setSelectedProject(project));
        dispatch(actions.asset.reset());
        dispatch(actions.plan.reset());
        dispatch(actions.project.reset());
        dispatch(actions.asset.get(project.code));
        if (project.plan != undefined) dispatch(actions.plan.get(project.plan));
        if (project.inspection != undefined && project.inspection !== -1)
            dispatch(actions.project.get(project.inspection));
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <span>{t('title')}</span>
                <span>{t('subtitle', { company: 'Georgia Pacific Corporation', location: 'Brewton, AL' })}</span>
                <div className={classes.status}>
                    {status.map(status => (
                        <ProjectListStatusCell
                            key={status.label}
                            {...status}
                            selected={status.filter === project_list.filter}
                            onClick={() => {
                                setPage(0);
                                dispatch(
                                    actions.project_list.list({
                                        filter: status.filter !== project_list.filter ? status.filter : 'upcoming',
                                        page: view === 'table' ? 0 : undefined,
                                        extraParams: extraParams.current,
                                    }),
                                );
                            }}
                        />
                    ))}
                </div>
            </div>
            <div className={classes.content}>
                <span className={classes.count}>{t('total', { count: project_list.count })}</span>
                {view === 'table' &&
                    (project_list.filter === 'upcoming' || project_list.filter === 'ready to inspect') && (
                        <ProjectListUpcomingBar
                            days={[30, 90, 180, 365]}
                            default={90}
                            selected={differenceInDays(project_list.interval.end, project_list.interval.start)}
                            onClick={days => {
                                setPage(0);
                                dispatch(
                                    actions.project_list.list({
                                        interval: { start: new Date(), end: addDays(new Date(), days) },
                                        page: view === 'table' ? 0 : undefined,
                                        extraParams: extraParams.current,
                                    }),
                                );
                            }}
                        />
                    )}
                <FilterBar
                    widgets={[
                        {
                            type: 'search',
                            label: t('filter-search'),
                            debounce: 300,
                            autocompleteKey: 'asset-list',
                            onChange: query => {
                                setPage(0);
                                dispatch(
                                    actions.project_list.list({ page: 0, query, extraParams: extraParams.current }),
                                );
                            },
                        },
                        {
                            type: 'checker',
                            label: t('filter-psm'),
                            checked: extraParams.current['psm'] === 'True',
                            onChange: checked => {
                                delete extraParams.current['psm'];
                                if (checked) extraParams.current['psm'] = 'True';
                                update();
                                dispatch(actions.project_list.list({ page: 0, extraParams: extraParams.current }));
                                setPage(0);
                            },
                        },
                        {
                            type: 'select',
                            label: t('filter-inspection-type'),
                            options: [...inspectionTypesCombined],
                            render: ((option: string) => (
                                <span style={{ textTransform: 'capitalize' }}>{option}</span>
                            )) as any,
                            selected: inspectionTypesCombined.map(type =>
                                (extraParams.current['inspection_type'] ?? '').split(',').includes(type),
                            ),
                            multi: true,
                            onSelected: selected => {
                                extraParams.current['inspection_type'] = inspectionTypesCombined
                                    .filter((_, i) => selected[i])
                                    .join(',');
                                update();
                                dispatch(actions.project_list.list({ page: 0, extraParams: extraParams.current }));
                                setPage(0);
                            },
                        },
                        {
                            type: 'select',
                            label: t('filter-status'),
                            options: [...projectStatus],
                            render: ((option: string) => (
                                <StatusIcon type={enumToStatusType[option]} icon={enumToIcon[option]}>
                                    {option
                                        .split(' ')
                                        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                                        .join(' ')}
                                </StatusIcon>
                            )) as any,
                            multi: true,
                            selected: projectStatus.map(status =>
                                (extraParams.current['status'] ?? '').split(',').includes(status),
                            ),
                            onSelected: selected => {
                                extraParams.current['status'] = projectStatus.filter((_, i) => selected[i]).join(',');
                                update();
                                dispatch(actions.project_list.list({ page: 0, extraParams: extraParams.current }));
                                setPage(0);
                            },
                        },
                        {
                            type: 'select',
                            label: t('filter-risk-level'),
                            options: [...probabilities, 'not-defined'],
                            render: ((option: string) => (
                                <Badge type={enumToStatusType[option]}>{option}</Badge>
                            )) as any,
                            multi: true,
                            selected: [...probabilities, 'not-defined'].map(
                                probability =>
                                    (extraParams.current['overall_risk'] ?? '').split(',').includes(probability) ||
                                    (probability === 'not-defined' &&
                                        (extraParams.current['overall_risk'] ?? '').includes('None')),
                            ),
                            onSelected: selected => {
                                extraParams.current['overall_risk'] = [...probabilities, 'not-defined']
                                    .filter((_, i) => selected[i])
                                    .map(value => (value != 'not-defined' ? value : 'None'))
                                    .join(',');
                                update();
                                dispatch(actions.project_list.list({ page: 0, extraParams: extraParams.current }));
                                setPage(0);
                            },
                        },
                        { type: 'clear' },
                        {
                            type: 'buttons',
                            buttons: [
                                {
                                    children: <Calendar />,
                                    selected: view === 'calendar',
                                    onClick: () => navigate('../calendar'),
                                },
                                { children: <List />, selected: view === 'table', onClick: () => navigate('../table') },
                            ],
                        },
                    ]}
                />

                {view === 'table' ? (
                    <div className={classes.reflow}>
                        <Table
                            loading={project_list.fetching ? numberOfItemsPerPage : 0}
                            columns={headers}
                            cells={project_list.projects.map(project => [
                                <ColumnEquipment project={project} />,
                                <ColumnType project={project} />,
                                <ColumnStatus project={project} />,
                                <ColumnTimeline project={project} type='external' />,
                                <ColumnTimeline project={project} type='internal' />,
                                <ColumnRisk project={project} />,
                                <ColumnAttributes project={project} />,
                            ])}
                            fixed={1}
                            paginator={{
                                page,
                                size: numberOfItemsPerPage,
                                total: project_list.count,
                                sizes: [10, 25, 50, 100],
                                onPaginationChange: (page, size) => {
                                    setPage(page);
                                    setNumberOfItemsPerPage(size);
                                    dispatch(
                                        actions.project_list.list({
                                            page,
                                            pageSize: size,
                                            extraParams: extraParams.current,
                                        }),
                                    );
                                },
                            }}
                            onRowClick={index => selectProject(project_list.projects[index])}
                            onSortColumn={(column, order) =>
                                dispatch(
                                    actions.project_list.list({
                                        page,
                                        sort: { key: headers[column].key, order },
                                        extraParams: extraParams.current,
                                    }),
                                )
                            }
                        />
                    </div>
                ) : (
                    <ProjectListCalendarView showCards={true} />
                )}
            </div>
            <Drawer
                open={selectedProject != undefined}
                onClose={() => {
                    dispatch(
                        actions.project_list.list({
                            filter: project_list.filter === '' ? 'upcoming' : project_list.filter,
                            page,
                            extraParams: extraParams.current,
                        }),
                    );
                    setSelectedProject(undefined);
                }}
            >
                {!!selectedProject && (
                    <ProjectEditor summary={selectedProject} onClose={() => setSelectedProject(undefined)} />
                )}
            </Drawer>
        </div>
    );
};
